import * as React from "react";
import { stories as ci } from "./data/catalog.js";
import {
  PAGE_STEP,
  clampPage,
  pageCacheKey,
  retainCachedPage,
  releaseCachedPage,
  getStoredPassword,
  COMIC_API,
  cachePage,
  isPageLocked,
  requiresProtectedSource,
  protectedPageSource,
  useVisibility,
  lockedImageUrl,
  publicPageUrl,
  releaseCountdown,
  isRecentRelease,
} from "./core.js";
// A transient image failure gets one fresh request, never a retry loop.
export function CoverImage({ src, alt }) {
  const [retry, setRetry] = React.useState(null);
  return (
    <img
      src={retry || src}
      alt={alt}
      className="comic-thumbnail"
      loading="lazy"
      decoding="async"
      onError={() => {
        if (!retry)
          setRetry(
            `${src}${src.includes("?") ? "&" : "?"}coverRetry=${Date.now()}`,
          );
      }}
    />
  );
}
export function LongStories({ stories: o = ci, onOpenStory: N }) {
  return (
    <section className="home-section" aria-labelledby="long-stories-title">
      <div className="home-section-heading">
        <p className="home-section-eyebrow">{"Featured format"}</p>
        <h2 id="long-stories-title" className="home-section-title">
          {"Long stories"}
        </h2>
        <p className="home-section-description">
          {
            "Read long-form EKYNOXE comics with free comic preview pages first, then continue the full story in the reader."
          }
        </p>
      </div>
      <div className="long-story-list">
        {o.map((x) => (
          <button
            type="button"
            className="comic-card long-story-card"
            onClick={() => N(x.id)}
            key={x.id}
          >
            <div className="comic-thumbnail-wrapper">
              <CoverImage
                key={x.thumbnail}
                src={x.thumbnail}
                alt={`Cover of ${x.name}`}
              />
            </div>
            <div className="comic-info">
              <h3 className="comic-title">{x.name}</h3>
              <p className="comic-pages">
                {x.pages}
                {" pages"}
              </p>
              <p className="long-story-meta">
                <span
                  className={
                    x.status === "Ongoing"
                      ? "long-story-status-badge long-story-status-badge--ongoing"
                      : "long-story-status-badge long-story-status-badge--completed"
                  }
                >
                  {x.status}
                </span>
              </p>
              <p className="long-story-summary">{x.summary}</p>
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}
export const Footer = ({ onLegalNoticeClick: o }) => (
  <footer className="app-footer">
    <button type="button" className="app-footer-legal-link" onClick={o}>
      {"Legal notice"}
    </button>
    <p className="app-footer-text">
      {"© Ekynoxe. All artworks are original and protected by copyright."}
      <br />
      {
        "Reposting or redistributing this content without permission is strictly prohibited."
      }
    </p>
  </footer>
);
export function QuickNavigation({
  currentPage: o,
  totalPages: N,
  step: x = PAGE_STEP,
  onNavigate: r,
}) {
  const [R, B] = React.useState(() => String(o)),
    w = o <= 1,
    te = o >= N,
    _ = () => {
      const L = Number.parseInt(R, 10);
      if (Number.isNaN(L)) {
        B(String(o));
        return;
      }
      r(L) || B(String(o));
    },
    z = (L) => {
      L.key === "Enter" && (L.preventDefault(), _());
    };
  return (
    <div className="reader-quick-nav" aria-label="Quick page navigation">
      <button
        type="button"
        className="button button-secondary reader-quick-nav-button"
        onClick={() => r(1)}
        disabled={w}
      >
        {"First"}
      </button>
      <button
        type="button"
        className="button button-secondary reader-quick-nav-button"
        onClick={() => r(clampPage(o - x, N))}
        disabled={w}
      >
        {"-"}
        {x}
      </button>
      <label className="reader-page-input-group" htmlFor="reader-page-input">
        <span className="reader-page-input-label">{"Page"}</span>
        <input
          id="reader-page-input"
          type="number"
          min={1}
          max={N}
          inputMode="numeric"
          className="reader-page-input"
          value={R}
          onChange={(L) => B(L.target.value)}
          onBlur={_}
          onKeyDown={z}
          aria-label="Jump to page"
        />
        <span className="reader-page-input-total">
          {"/ "}
          {N}
        </span>
      </label>
      <button
        type="button"
        className="button button-secondary reader-quick-nav-button"
        onClick={() => r(clampPage(o + x, N))}
        disabled={te}
      >
        {"+"}
        {x}
      </button>
      <button
        type="button"
        className="button button-secondary reader-quick-nav-button"
        onClick={() => r(N)}
        disabled={te}
      >
        {"Last"}
      </button>
    </div>
  );
}
export function ProtectedPage({
  comicId: o,
  page: N,
  alt: x,
  className: r,
  onUnauthorized: R,
}) {
  const [B, w] = React.useState(null),
    [te, _] = React.useState(!1);
  return (
    React.useEffect(() => {
      let z = !0,
        L = null;
      const q = pageCacheKey(o, N),
        ie = retainCachedPage(q);
      if (ie)
        return (
          w(ie),
          _(!1),
          () => {
            releaseCachedPage(q, ie);
          }
        );
      (w(null), _(!1));
      const ze = getStoredPassword();
      if (!ze) return (z && (R ? R() : _(!0)), () => {});
      const Ae = new AbortController();
      return (
        (async () => {
          try {
            _(!1);
            const qe = await fetch(
              `${COMIC_API}?comicId=${encodeURIComponent(o)}&page=${encodeURIComponent(String(N))}`,
              {
                method: "GET",
                headers: {
                  "X-Comic-Password": ze,
                },
                signal: Ae.signal,
              },
            );
            if (!qe.ok) {
              z && (qe.status === 401 && R ? R() : _(!0));
              return;
            }
            const Ee = await qe.blob();
            ((L = cachePage(q, URL.createObjectURL(Ee), 1)), z && w(L));
          } catch {
            z && _(!0);
          }
        })(),
        () => {
          ((z = !1), Ae.abort(), releaseCachedPage(q, L));
        }
      );
    }, [o, N, R]),
    te ? (
      <div className={`reader-paid-error${r ? ` ${r}` : ""}`}>
        <span className="reader-paid-error-text">
          {
            "Unable to load this page. Please check your connection and try again."
          }
        </span>
      </div>
    ) : B ? (
      <img src={B} alt={x} className={r} />
    ) : (
      <div className={`reader-paid-loading${r ? ` ${r}` : ""}`}>
        <div className="reader-paid-spinner" />
      </div>
    )
  );
}
export function VerticalPage({
  comic: o,
  pageNumber: N,
  hasFullAccess: x,
  onUnauthorized: r,
  onLockedClick: R,
}) {
  const B = isPageLocked(o, N, x),
    w = requiresProtectedSource(o, N),
    te = protectedPageSource(o, N),
    { ref: _, isVisible: z } = useVisibility("800px");
  return (
    <div
      ref={_}
      className="reader-image-container reader-image-container--static"
      data-page={N}
    >
      {B ? (
        <button
          type="button"
          className="reader-locked reader-locked--emoji"
          onClick={R}
        >
          <img
            src={lockedImageUrl()}
            alt="Locked page – click to unlock"
            className="reader-locked-preview"
          />
        </button>
      ) : x && w ? (
        z ? (
          <ProtectedPage
            comicId={te.comicId}
            page={te.page}
            alt={`Page ${N} of ${o.name}`}
            className="reader-image"
            onUnauthorized={r}
          />
        ) : (
          <div className="reader-paid-loading">
            <div className="reader-paid-spinner" />
          </div>
        )
      ) : (
        <img
          src={publicPageUrl(o, N)}
          alt={`Page ${N} of ${o.name}`}
          className="reader-image"
          loading="lazy"
        />
      )}
    </div>
  );
}
export function ComicGrid({ comics: o, nowMs: N, onOpenComic: x }) {
  return (
    <div className="comic-list">
      {o.map((r) => {
        const R = releaseCountdown(r.releaseAt, N);
        return (
          <button
            type="button"
            className={`comic-card${r.isReleased ? "" : " comic-card--unreleased"}`}
            onClick={r.isReleased ? () => x(r.id) : void 0}
            aria-disabled={r.isReleased ? void 0 : "true"}
            disabled={!r.isReleased}
            key={r.id}
          >
            <div className="comic-thumbnail-wrapper">
              <CoverImage
                key={r.thumbnail}
                src={r.thumbnail}
                alt={`Cover of ${r.name}`}
              />
            </div>
            <div className="comic-info">
              <h3 className="comic-title">{r.name}</h3>
              <p className="comic-pages">
                {r.pages}
                {" pages"}
              </p>
              <p className="comic-release-date">
                {r.releaseDate}
                {(r.video ||
                  !r.isReleased ||
                  isRecentRelease(r.releasedAt, N)) && (
                  <span className="comic-release-labels">
                    {r.isReleased ? (
                      isRecentRelease(r.releasedAt, N) && (
                        <span className="comic-new-label">{"New"}</span>
                      )
                    ) : (
                      <span
                        className={
                          R ? "comic-countdown" : "comic-unreleased-inline"
                        }
                      >
                        {" • "}
                        {R ?? "Coming soon"}
                      </span>
                    )}
                    {r.video && (
                      <span
                        className="comic-video-label"
                        title="Includes an animated video"
                      >
                        Video
                      </span>
                    )}
                  </span>
                )}
              </p>
            </div>
          </button>
        );
      })}
    </div>
  );
}
